import { useCallback, useMemo } from 'react';

import { ConsumerStudentFee } from '@givebacks/api/student';
import { useFormyValidator } from '@givebacks/formy';
import { CheckoutButtons } from '@givebacks/shop';
import { Modal, ModalProps, PartialPaymentForm, type PartialPaymentFormReturn, Stack } from 'ui';

import { useFeeCartHandler } from '../../../hooks/useFeeCartHandler';

interface AddPartialPaymentModalProps extends ModalProps {
  studentFee: ConsumerStudentFee;
  /**
   * Store Item Id of the fee
   */
  storeItemId: string;

  /**
   * Initial amount to set in the form, should either be the remaining balance or the amount already in the cart
   */
  initialAmount?: number;

  /**
   * Existing cart info is used to provide the cartId and cartItemId to the useAddFeeToCartHandler hook
   * if both cartId and cartItemId are provided the modal will default to updating the cartItem
   */
  existingCartInfo?: {
    cartId?: string;
    cartItemId?: string;
  };

  /**
   * If true, the modal will be used to pay('buy now') now instead of adding to cart
   */
  isPayNow?: boolean;
}

interface AddPartialPaymentModalContentProps
  extends Pick<
    AddPartialPaymentModalProps,
    'studentFee' | 'storeItemId' | 'existingCartInfo' | 'initialAmount' | 'onClose' | 'isPayNow'
  > {}

interface PartialPaymentFooterProps
  extends Pick<
    AddPartialPaymentModalProps,
    'studentFee' | 'storeItemId' | 'existingCartInfo' | 'onClose' | 'isPayNow'
  > {}

const PartialPaymentFooter = ({
  studentFee,
  storeItemId,
  existingCartInfo,
  onClose,
  isPayNow,
}: PartialPaymentFooterProps) => {
  const { cartId, cartItemId } = existingCartInfo ?? {};

  const { useGetForm } = useFormyValidator<{
    'partial-payment-form': PartialPaymentFormReturn;
  }>();
  const form = useGetForm('partial-payment-form');

  const { handleUpdateExistingCartItem, cartItems, isLoading } = useFeeCartHandler({
    feeId: storeItemId,
    causeId: studentFee.cause_id,
    // Although these values are not optional, they are not immediately available when the form is created, so we need to check if they are defined
    price: form?.values?.amount,
    studentFeeId: studentFee.uuid,
    cartHandlerOptions: {
      onCartCreate: onClose,
    },
  });

  const isUpdatingExistingCart = useMemo(() => {
    return cartId && cartItemId;
  }, [cartId, cartItemId]);

  const handleAddToCart = useCallback(() => {
    if (cartId && cartItemId) {
      if (form.validate().hasErrors) {
        return;
      }
      handleUpdateExistingCartItem({
        cartId,
        cartItemId,
      });
    }
  }, [cartId, cartItemId, form, handleUpdateExistingCartItem]);

  return (
    <CheckoutButtons
      storeId={studentFee.cause_id}
      storeItemId={storeItemId}
      cartItems={cartItems}
      validateOnClick={true}
      onCartCreate={onClose}
      buyNowVisible={isPayNow}
      addToCartVisible={!isPayNow}
      buyNowProps={{
        buttonProps: {
          children: 'Pay Now',
        },
      }}
      addToCartProps={{
        onAddToCartClick: isUpdatingExistingCart ? handleAddToCart : undefined,
        buttonProps: {
          loading: isUpdatingExistingCart ? isLoading : undefined,
          variant: 'filled',
          children: 'Add to Cart',
        },
      }}
    />
  );
};

const AddPartialPaymentModalContent = ({
  studentFee,
  storeItemId,
  initialAmount,
  existingCartInfo,
  onClose,
  isPayNow,
}: AddPartialPaymentModalContentProps) => {
  return (
    <form>
      <Stack>
        <PartialPaymentForm
          initialAmount={initialAmount ?? studentFee.amount_remaining}
          maxAmount={studentFee.amount_remaining}
        />
        <PartialPaymentFooter
          studentFee={studentFee}
          storeItemId={storeItemId}
          onClose={onClose}
          isPayNow={isPayNow}
          existingCartInfo={existingCartInfo}
        />
      </Stack>
    </form>
  );
};

const AddPartialPaymentModal = ({
  studentFee,
  storeItemId,
  initialAmount,
  existingCartInfo,
  isPayNow = false,
  ...props
}: AddPartialPaymentModalProps) => {
  return (
    <Modal title="How much would you like to pay?" {...props}>
      <AddPartialPaymentModalContent
        studentFee={studentFee}
        storeItemId={storeItemId}
        initialAmount={initialAmount}
        existingCartInfo={existingCartInfo}
        onClose={props.onClose}
        isPayNow={isPayNow}
      />
    </Modal>
  );
};

export { AddPartialPaymentModal };
